<script setup lang="ts">
import {
  CircleOff,
  HeartPulse,
  LoaderCircle,
  LogIn,
  LogOut,
  MessageSquareText,
  Plus,
  RefreshCw,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-vue-next"
import { computed, onBeforeUnmount, onMounted, ref } from "vue"

import AppActionDialog from "../../components/AppActionDialog.vue"
import AppSelect from "../../components/AppSelect.vue"
import { apiRequest } from "../../lib/api"
import { formatShanghaiTime } from "../../lib/datetime"
import type { ProviderAccountItem } from "../../lib/types"
import { useSessionStore } from "../../stores/session"

type ManagedPlatform = "rednote" | "zhihu" | "douban" | "douyin" | "weibo" | "weibo_web" | "bilibili" | "xueqiu" | "reddit" | "twitter" | "xianyu" | "jd" | "nowcoder" | "instagram" | "maimai" | "jianyu" | "dongchedi" | "ctrip" | "linkedin"
type PlatformFilter = "all" | ManagedPlatform

const session = useSessionStore()
const accounts = ref<ProviderAccountItem[]>([])
const platformFilter = ref<PlatformFilter>("all")
const loading = ref(true)
const error = ref("")
const creating = ref(false)
const submitting = ref(false)
const importingLegacy = ref(false)
const actionAccountId = ref("")
const expectedProviderLoginExtensionVersion = "0.16.0"
const providerLoginExtensionReady = ref(false)
const providerLoginExtensionVersion = ref("")
const providerBrowserFlow = ref<{ flowId: string; account: ProviderAccountItem } | null>(null)
const providerBrowserError = ref("")
const providerBrowserProgress = ref("")
const deviceImportAccount = ref<ProviderAccountItem | null>(null)
const deviceImportJson = ref("")
const deviceImportError = ref("")
async function importBilibiliDevice(): Promise<void> {
  const account = deviceImportAccount.value
  if (!account) return
  actionAccountId.value = account.id
  deviceImportError.value = ""
  try {
    const updated = await apiRequest<ProviderAccountItem>(`/v1/admin/provider-accounts/${account.id}/login/device`, {
      method: "POST", csrfToken: session.csrfToken,
      body: { state_json: deviceImportJson.value, login_identifier: account.login_identifier || account.display_name },
    })
    replaceAccount(updated)
    deviceImportJson.value = ""
    deviceImportAccount.value = null
  } catch (cause) {
    deviceImportError.value = cause instanceof Error ? cause.message : "设备导入失败"
  } finally { actionAccountId.value = "" }
}
type SmsCaptchaResult = { id: string; gee_challenge: string; gee_validate: string; gee_seccode: string }
const captchaAccount = ref<ProviderAccountItem | null>(null)
const captchaFrame = ref<HTMLIFrameElement | null>(null)
const captchaError = ref("")
const captchaUrl = computed(() => {
  const challenge = captchaAccount.value?.sms_captcha
  if (!challenge) return ""
  return `/bilibili-captcha.html#${new URLSearchParams({ ...challenge, origin: window.location.origin })}`
})
async function handleSmsCaptchaMessage(event: MessageEvent): Promise<void> {
  const account = captchaAccount.value
  const data = event.data
  if (!account?.sms_captcha || event.source !== captchaFrame.value?.contentWindow ||
      event.origin !== "null" || data?.source !== "byrecc-bilibili-captcha" ||
      data.id !== account.sms_captcha.id || actionAccountId.value) return
  if (data.type === "error") {
    captchaError.value = typeof data.message === "string" ? data.message : "人机验证失败，请重试"
    return
  }
  if (data.type !== "success") return
  const fields = data.result
  if (!fields || ![fields.gee_challenge, fields.gee_validate, fields.gee_seccode]
      .every(value => typeof value === "string" && value.length > 0 && value.length <= 128)) return
  await sendSmsCode(account, undefined, { id: account.sms_captcha.id,
    gee_challenge: fields.gee_challenge, gee_validate: fields.gee_validate, gee_seccode: fields.gee_seccode })
}

const smsLoginAccount = ref<ProviderAccountItem | null>(null)
const smsCode = ref("")
const smsCodeValid = computed(() => (smsLoginAccount.value?.platform === "ctrip" ? /^\d{6}$/ : /^\d{4,6}$/).test(smsCode.value))
const smsPhoneAccount = ref<ProviderAccountItem | null>(null)
const smsPhone = ref("")
const smsError = ref("")
const smsVerifying = ref(false)
const actionDialog = ref<{
  kind: "import" | "logout" | "cooldown" | "delete"
  account?: ProviderAccountItem
} | null>(null)
const cooldownSeconds = ref("300")
const platformOptions = [
  { value: "rednote", label: "小红书 / RedNote" },
  { value: "zhihu", label: "知乎 / Zhihu" },
  { value: "douban", label: "豆瓣 / Douban" },
  { value: "douyin", label: "抖音 / Douyin" },
  { value: "weibo", label: "微博移动接口 / Weibo CLI" },
  { value: "weibo_web", label: "微博网页接口 / Weibo Web" },
  { value: "bilibili", label: "哔哩哔哩 / Bilibili" },
  { value: "xueqiu", label: "雪球 / Xueqiu" },
  { value: "reddit", label: "Reddit" },
  { value: "twitter", label: "Twitter / X" },
  { value: "xianyu", label: "闲鱼 / Xianyu" },
  { value: "jd", label: "京东 / JD" },
  { value: "nowcoder", label: "牛客 / Nowcoder" },
  { value: "instagram", label: "Instagram" },
  { value: "maimai", label: "脉脉 / Maimai" },
  { value: "jianyu", label: "剑鱼标讯 / Jianyu" },
  { value: "dongchedi", label: "懂车帝 / Dongchedi" },
  { value: "ctrip", label: "携程 / Ctrip" },
  { value: "linkedin", label: "LinkedIn" },
]
const cookiePlatforms: ManagedPlatform[] = ["weibo_web", "douyin", "xueqiu", "reddit", "twitter", "xianyu", "jd", "nowcoder", "instagram", "maimai", "jianyu", "dongchedi", "linkedin"]
const browserLoginPlatforms: ManagedPlatform[] = ["weibo_web", "douyin", "xueqiu", "reddit", "twitter", "xianyu", "jd", "nowcoder", "instagram", "maimai", "jianyu", "dongchedi", "linkedin"]
const createForm = ref({
  platform: "rednote" as ManagedPlatform,
  state_name: "",
  display_name: "",
  username: "",
  password: "",
  state_json: "",
  zone: "86",
  weight: 1,
  pool_enabled: false,
})

const filteredAccounts = computed(() =>
  platformFilter.value === "all"
    ? accounts.value
    : accounts.value.filter((item) => item.platform === platformFilter.value),
)
const platformCounts = computed(() => ({
  all: accounts.value.length,
  rednote: accounts.value.filter((item) => item.platform === "rednote").length,
  zhihu: accounts.value.filter((item) => item.platform === "zhihu").length,
  douban: accounts.value.filter((item) => item.platform === "douban").length,
  douyin: accounts.value.filter((item) => item.platform === "douyin").length,
  weibo: accounts.value.filter((item) => item.platform === "weibo").length,
  weibo_web: accounts.value.filter((item) => item.platform === "weibo_web").length,
  bilibili: accounts.value.filter((item) => item.platform === "bilibili").length,
  xueqiu: accounts.value.filter((item) => item.platform === "xueqiu").length,
  reddit: accounts.value.filter((item) => item.platform === "reddit").length,
  twitter: accounts.value.filter((item) => item.platform === "twitter").length,
  xianyu: accounts.value.filter((item) => item.platform === "xianyu").length,
  jd: accounts.value.filter((item) => item.platform === "jd").length,
  nowcoder: accounts.value.filter((item) => item.platform === "nowcoder").length,
  instagram: accounts.value.filter((item) => item.platform === "instagram").length,
  maimai: accounts.value.filter((item) => item.platform === "maimai").length,
  jianyu: accounts.value.filter((item) => item.platform === "jianyu").length,
  dongchedi: accounts.value.filter((item) => item.platform === "dongchedi").length,
  ctrip: accounts.value.filter((item) => item.platform === "ctrip").length,
  linkedin: accounts.value.filter((item) => item.platform === "linkedin").length,
}))
const poolAccountCount = computed(() => accounts.value.filter((item) => item.pool_enabled).length)
const loggedInCount = computed(() => accounts.value.filter((item) => item.login_status === "logged_in").length)

let providerBrowserTimer: ReturnType<typeof setTimeout> | undefined
let providerBrowserSubmissionFlowId = ""

onMounted(() => {
  window.addEventListener("message", handleProviderExtensionMessage)
  window.addEventListener("message", handleSmsCaptchaMessage)
  window.postMessage(
    { source: "byrecc-console", type: "BYRECC_PROVIDER_LOGIN_EXTENSION_PING" },
    window.location.origin,
  )
  void load()
})

onBeforeUnmount(() => {
  window.removeEventListener("message", handleProviderExtensionMessage)
  window.removeEventListener("message", handleSmsCaptchaMessage)
  if (providerBrowserTimer) window.clearTimeout(providerBrowserTimer)
})

function handleProviderExtensionMessage(event: MessageEvent): void {
  if (event.source !== window || event.origin !== window.location.origin) return
  const message = event.data as Record<string, unknown> | null
  if (!message || message.source !== "byrecc-provider-login-extension") return
  if (message.type === "BYRECC_PROVIDER_LOGIN_EXTENSION_READY") {
    providerLoginExtensionVersion.value =
      typeof message.version === "string" ? message.version : "旧版"
    providerLoginExtensionReady.value =
      providerLoginExtensionVersion.value === expectedProviderLoginExtensionVersion
    return
  }
  const flow = providerBrowserFlow.value
  if (!flow || message.flowId !== flow.flowId) return
  if (message.type === "BYRECC_PROVIDER_LOGIN_PROGRESS") {
    providerBrowserProgress.value =
      typeof message.message === "string" ? message.message : "正在等待平台登录凭证"
    return
  }
  if (message.type === "BYRECC_PROVIDER_LOGIN_ERROR") {
    finishProviderBrowserFlow()
    providerBrowserError.value =
      typeof message.message === "string" ? message.message : "平台浏览器登录失败"
    return
  }
  if (
    message.type !== "BYRECC_PROVIDER_LOGIN_COMPLETE" ||
    message.platform !== flow.account.platform
  ) return
  const cookies = message.cookies
  if (!cookies || typeof cookies !== "object" || Array.isArray(cookies)) {
    finishProviderBrowserFlow()
    providerBrowserError.value = "扩展返回了无效的平台登录状态"
    return
  }
  if (
    ["weibo_web", "douyin", "xianyu", "jd", "nowcoder", "instagram", "maimai", "jianyu", "dongchedi", "ctrip", "linkedin"].includes(flow.account.platform) &&
    (!Array.isArray(message.cookieJar) || typeof message.userAgent !== "string")
  ) {
    finishProviderBrowserFlow()
    providerBrowserError.value = `扩展返回的${platformLabel(flow.account.platform)} Cookie jar 或浏览器标识无效`
    return
  }
  const providerExtras = message.providerExtras
  const xianyuSecurity =
    providerExtras && typeof providerExtras === "object" && !Array.isArray(providerExtras)
      ? (providerExtras as Record<string, unknown>).security_params
      : null
  if (
    flow.account.platform === "xianyu" &&
    (!xianyuSecurity ||
      typeof xianyuSecurity !== "object" ||
      Array.isArray(xianyuSecurity) ||
      !["uab", "umid", "et"].every(
        (name) => typeof (xianyuSecurity as Record<string, unknown>)[name] === "string",
      ))
  ) {
    finishProviderBrowserFlow()
    providerBrowserError.value = "扩展尚未取得闲鱼安全参数，请等待搜索结果加载完成后重新检测"
    return
  }
  if (
    flow.account.platform === "jd" &&
    (!providerExtras ||
      typeof providerExtras !== "object" ||
      Array.isArray(providerExtras) ||
      typeof (providerExtras as Record<string, unknown>).js_token !== "string")
  ) {
    finishProviderBrowserFlow()
    providerBrowserError.value = "扩展尚未取得京东设备令牌，请等待搜索页加载完成后重新检测"
    return
  }
  if (
    flow.account.platform === "nowcoder" &&
    (!providerExtras ||
      typeof providerExtras !== "object" ||
      Array.isArray(providerExtras) ||
      typeof (providerExtras as Record<string, unknown>).user_id !== "number")
  ) {
    finishProviderBrowserFlow()
    providerBrowserError.value = "扩展尚未取得牛客登录用户 ID，请等待首页加载完成后重新检测"
    return
  }
  if (
    flow.account.platform === "maimai" &&
    (!providerExtras ||
      typeof providerExtras !== "object" ||
      Array.isArray(providerExtras) ||
      typeof (providerExtras as Record<string, unknown>).user_id !== "string")
  ) {
    finishProviderBrowserFlow()
    providerBrowserError.value = "扩展尚未取得脉脉登录用户 ID，请等待搜索页加载完成后重新检测"
    return
  }
  if (providerBrowserSubmissionFlowId === flow.flowId) return
  providerBrowserSubmissionFlowId = flow.flowId
  providerBrowserProgress.value = "已检测到平台凭证，正在提交服务端校验…"
  void completeProviderBrowserLogin(flow.account, message)
}

function startProviderBrowserLogin(account: ProviderAccountItem): void {
  providerBrowserError.value = ""
  if (!providerLoginExtensionReady.value) {
    providerBrowserError.value = providerLoginExtensionVersion.value
      ? `当前连接助手为 v${providerLoginExtensionVersion.value}，请升级到 v${expectedProviderLoginExtensionVersion} 后刷新本页`
      : "请先安装平台登录助手并刷新本页"
    return
  }
  providerBrowserProgress.value = "正在准备全新的独立登录窗口…"
  const flowId = crypto.randomUUID()
  providerBrowserFlow.value = { flowId, account }
  actionAccountId.value = account.id
  window.postMessage(
    {
      source: "byrecc-console",
      type: "BYRECC_PROVIDER_LOGIN_START",
      flowId,
      platform: account.platform,
    },
    window.location.origin,
  )
  providerBrowserTimer = window.setTimeout(() => {
    if (providerBrowserFlow.value?.flowId !== flowId) return
    finishProviderBrowserFlow()
    providerBrowserError.value = `${platformLabel(account.platform)}登录已超时，请重试`
  }, 10 * 60 * 1000)
}

function captureProviderBrowserLogin(): void {
  const flow = providerBrowserFlow.value
  if (!flow) return
  providerBrowserError.value = ""
  providerBrowserProgress.value = "正在重新检测平台登录状态…"
  window.postMessage(
    {
      source: "byrecc-console",
      type: "BYRECC_PROVIDER_LOGIN_CAPTURE",
      flowId: flow.flowId,
    },
    window.location.origin,
  )
}

async function completeProviderBrowserLogin(
  account: ProviderAccountItem,
  message: Record<string, unknown>,
): Promise<void> {
  try {
    const cookies = message.cookies as Record<string, string>
    const state = ["weibo_web", "douyin", "xianyu", "jd", "nowcoder", "instagram", "maimai", "jianyu", "dongchedi", "ctrip", "linkedin"].includes(account.platform)
      ? {
          schema_version: 1,
          ...(account.platform === "weibo_web" ? { provider: "web_cookie" } : {}),
          user_agent: message.userAgent,
          cookies: message.cookieJar,
          ...(account.platform === "jd"
            ? {
                js_token: (message.providerExtras as Record<string, unknown>).js_token,
                signer_storage:
                  (message.providerExtras as Record<string, unknown>).signer_storage || {},
              }
            : {}),
          ...(account.platform === "douyin"
            ? {
                query_template:
                  (message.providerExtras as Record<string, unknown>).query_template || {},
                browser_fingerprint:
                  (message.providerExtras as Record<string, unknown>).browser_fingerprint || "",
                request_headers:
                  (message.providerExtras as Record<string, unknown>).request_headers || {},
              }
            : {}),
          ...(account.platform === "xianyu"
            ? {
                security_params:
                  (message.providerExtras as Record<string, unknown>).security_params,
              }
            : {}),
          ...(account.platform === "nowcoder"
            ? { user_id: (message.providerExtras as Record<string, unknown>).user_id }
            : {}),
          ...(account.platform === "maimai"
            ? { user_id: (message.providerExtras as Record<string, unknown>).user_id }
            : {}),
        }
      : { cookies }
    const updated = await apiRequest<ProviderAccountItem>(
      `/v1/admin/provider-accounts/${account.id}/login/browser`,
      {
        method: "POST",
        csrfToken: session.csrfToken,
        body: {
          state_json: JSON.stringify(state),
          login_identifier: account.login_identifier || account.display_name,
        },
      },
    )
    replaceAccount(updated)
    providerBrowserError.value = ""
    window.postMessage(
      {
        source: "byrecc-console",
        type: "BYRECC_PROVIDER_LOGIN_FINISH",
        flowId: providerBrowserFlow.value?.flowId,
      },
      window.location.origin,
    )
    finishProviderBrowserFlow()
  } catch (cause) {
    if (providerBrowserSubmissionFlowId === providerBrowserFlow.value?.flowId) {
      providerBrowserSubmissionFlowId = ""
    }
    const message = cause instanceof Error ? cause.message : "平台登录状态保存失败"
    providerBrowserProgress.value = ""
    providerBrowserError.value = account.platform === "douyin"
      ? `${message}。抖音服务端搜索校验尚未通过；请在抖音窗口完成滑块并搜索一次，窗口会保留并自动重新检测，也可点击“重新检测”。`
      : `${message}。请在${platformLabel(account.platform)}窗口重新登录，窗口无需关闭。`
  }
}

function finishProviderBrowserFlow(): void {
  if (providerBrowserTimer) window.clearTimeout(providerBrowserTimer)
  providerBrowserTimer = undefined
  providerBrowserSubmissionFlowId = ""
  actionAccountId.value = ""
  providerBrowserFlow.value = null
  providerBrowserProgress.value = ""
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ""
  try {
    accounts.value = await apiRequest<ProviderAccountItem[]>("/v1/admin/provider-accounts")
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "加载平台账号失败"
  } finally {
    loading.value = false
  }
}

async function createAccount(): Promise<void> {
  submitting.value = true
  error.value = ""
  try {
    const created = await apiRequest<ProviderAccountItem>("/v1/admin/provider-accounts", {
      method: "POST",
      csrfToken: session.csrfToken,
      body: {
        ...createForm.value,
        ...(createForm.value.platform === "ctrip" ? { state_json: "" } : {}),
      },
    })
    accounts.value.push(created)
    creating.value = false
    createForm.value = {
      platform: "rednote",
      state_name: "",
      display_name: "",
      username: "",
      password: "",
      state_json: "",
      zone: "86",
      weight: 1,
      pool_enabled: false,
    }
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "创建账号失败"
  } finally {
    submitting.value = false
  }
}

async function importLegacyAccounts(): Promise<void> {
  importingLegacy.value = true
  error.value = ""
  try {
    const imported = await apiRequest<ProviderAccountItem[]>("/v1/admin/provider-accounts/import-legacy", {
      method: "POST",
      csrfToken: session.csrfToken,
    })
    accounts.value.push(...imported)
    if (imported.length === 0) error.value = "未发现可导入的旧默认账号，或对应账号已经导入。"
    actionDialog.value = null
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "导入旧账号失败"
  } finally {
    importingLegacy.value = false
  }
}

async function patchAccount(account: ProviderAccountItem, patch: Record<string, unknown>): Promise<void> {
  try {
    const updated = await apiRequest<ProviderAccountItem>(`/v1/admin/provider-accounts/${account.id}`, {
      method: "PATCH",
      csrfToken: session.csrfToken,
      body: patch,
    })
    replaceAccount(updated)
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "更新账号失败"
  }
}

async function logout(account: ProviderAccountItem): Promise<void> {
  await runAction(account, "logout")
  actionDialog.value = null
}

async function cooldown(account: ProviderAccountItem): Promise<void> {
  const seconds = Number(cooldownSeconds.value)
  if (!Number.isFinite(seconds) || seconds < 0) return
  await runAction(account, "cooldown", { seconds, reason: "管理员手动冷却" })
  actionDialog.value = null
}

async function deleteAccount(account: ProviderAccountItem): Promise<void> {
  error.value = ""
  actionAccountId.value = account.id
  try {
    await apiRequest<void>(`/v1/admin/provider-accounts/${account.id}`, {
      method: "DELETE",
      csrfToken: session.csrfToken,
    })
    accounts.value = accounts.value.filter((item) => item.id !== account.id)
    actionDialog.value = null
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "删除账号失败"
  } finally {
    actionAccountId.value = ""
  }
}

async function confirmProviderAction(): Promise<void> {
  const pending = actionDialog.value
  if (!pending) return
  if (pending.kind === "import") {
    await importLegacyAccounts()
  } else if (pending.kind === "logout" && pending.account) {
    await logout(pending.account)
  } else if (pending.kind === "cooldown" && pending.account) {
    await cooldown(pending.account)
  } else if (pending.kind === "delete" && pending.account) {
    await deleteAccount(pending.account)
  }
}

async function runAction(account: ProviderAccountItem, action: string, body?: unknown): Promise<void> {
  error.value = ""
  actionAccountId.value = account.id
  try {
    const updated = await apiRequest<ProviderAccountItem>(`/v1/admin/provider-accounts/${account.id}/${action}`, {
      method: "POST",
      csrfToken: session.csrfToken,
      body,
    })
    replaceAccount(updated)
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : "账号操作失败"
  } finally {
    actionAccountId.value = ""
  }
}

function ctripAccountPhone(account: ProviderAccountItem): string | undefined {
  const phone = (account.login_identifier ?? "").trim().replace(/^\+86/, "")
  return /^1\d{10}$/.test(phone) ? phone : undefined
}

async function sendSmsCode(account: ProviderAccountItem, phone?: string, captcha?: SmsCaptchaResult): Promise<void> {
  if (["ctrip", "bilibili"].includes(account.platform) && phone === undefined && !account.sms_login_ready) {
    phone = ctripAccountPhone(account)
    if (!phone) {
      smsPhone.value = ""
      smsPhoneAccount.value = account
      error.value = ""
      return
    }
  }
  error.value = ""
  smsError.value = ""
  actionAccountId.value = account.id
  try {
    const updated = await apiRequest<ProviderAccountItem>(
      `/v1/admin/provider-accounts/${account.id}/login/sms/send`,
      {
        method: "POST",
        csrfToken: session.csrfToken,
        ...(captcha ? { body: { captcha } } : phone ? { body: { phone } } : {}),
      },
    )
    replaceAccount(updated)
    smsPhoneAccount.value = null
    captchaError.value = ""
    if (updated.sms_captcha) {
      captchaAccount.value = updated
      return
    }
    captchaAccount.value = null
    smsLoginAccount.value = updated
    smsCode.value = ""
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : "发送验证码失败"
    if (captchaAccount.value) captchaError.value = message
    else error.value = message
  } finally {
    actionAccountId.value = ""
  }
}

async function verifySmsCode(): Promise<void> {
  const account = smsLoginAccount.value
  if (!account || !smsCodeValid.value) return
  smsError.value = ""
  smsVerifying.value = true
  try {
    const updated = await apiRequest<ProviderAccountItem>(
      `/v1/admin/provider-accounts/${account.id}/login/sms/verify`,
      {
        method: "POST",
        csrfToken: session.csrfToken,
        body: { code: smsCode.value },
      },
    )
    replaceAccount(updated)
    smsLoginAccount.value = null
    smsCode.value = ""
  } catch (cause) {
    smsError.value = cause instanceof Error ? cause.message : "验证码登录失败"
  } finally {
    smsVerifying.value = false
  }
}

async function resendSmsCode(): Promise<void> {
  const account = smsLoginAccount.value
  if (!account) return
  smsLoginAccount.value = null
  await sendSmsCode(account)
}

function replaceAccount(updated: ProviderAccountItem): void {
  accounts.value = accounts.value.map((item) => (item.id === updated.id ? updated : item))
}

function formatDate(value: string | null): string {
  if (!value) return "—"
  return formatShanghaiTime(value, { dateStyle: "short", timeStyle: "short" })
}

function loginStatusLabel(status: ProviderAccountItem["login_status"]): string {
  return {
    logged_out: "未登录",
    logging_in: "登录中",
    logged_in: "已登录",
    expired: "已过期",
    error: "异常",
  }[status]
}

function platformLabel(platform: ProviderAccountItem["platform"]): string {
  return { rednote: "小红书", zhihu: "知乎", douban: "豆瓣", douyin: "抖音", weibo: "微博 CLI", weibo_web: "微博 Web", bilibili: "哔哩哔哩", xueqiu: "雪球", reddit: "Reddit", twitter: "Twitter / X", xianyu: "闲鱼", jd: "京东", nowcoder: "牛客", instagram: "Instagram", maimai: "脉脉", jianyu: "剑鱼标讯", dongchedi: "懂车帝", ctrip: "携程", linkedin: "LinkedIn" }[platform]
}

function platformMark(platform: ProviderAccountItem["platform"]): string {
  return { rednote: "RED", zhihu: "知", douban: "豆", douyin: "抖", weibo: "博", weibo_web: "WEB", bilibili: "哔", xueqiu: "雪", reddit: "R", twitter: "X", xianyu: "闲", jd: "京", nowcoder: "牛", instagram: "IG", maimai: "脉", jianyu: "剑", dongchedi: "车", ctrip: "旅", linkedin: "in" }[platform]
}

function accountSuccessRate(account: ProviderAccountItem): string {
  if (!account.calls_24h) return "—"
  return `${Math.round((account.succeeded_calls_24h / account.calls_24h) * 100)}%`
}

function credentialStatus(account: ProviderAccountItem): string {
  if (["ctrip", "bilibili"].includes(account.platform)) {
    return account.login_status === "logged_in" ? "登录凭证已加密保存" : account.sms_login_pending ? "验证码已发送，可继续输入验证码" : (account.sms_login_ready || ctripAccountPhone(account)) ? "手机号已配置，可验证码登录" : "点击验证码登录并填写手机号"
  }
  if (cookiePlatforms.includes(account.platform)) {
    if (account.login_status === "logged_in") return "Cookie 已校验并加密"
    return account.credentials_ready ? "Cookie 已加密，等待校验" : "等待浏览器登录"
  }
  if (account.platform === "zhihu") {
    return account.password_login_ready ? "登录凭据已准备" : "缺少登录凭据"
  }
  if (account.platform === "weibo") {
    if (account.login_status === "logged_in") return "独立设备与会话已加密"
    return account.sms_login_ready ? "手机号已配置，可验证码登录" : "缺少登录手机号"
  }
  if (account.platform === "douban") {
    if (account.login_status === "logged_in") return "独立设备与令牌已加密"
    return account.sms_login_ready ? "手机号已配置，可验证码登录" : "缺少登录手机号"
  }
  if (account.password_login_ready) return "手机号及密码已配置"
  if (account.sms_login_ready) return "手机号已配置，可验证码登录"
  return "缺少登录手机号"
}
</script>

<template>
  <section class="admin-page">
    <div class="admin-page-heading">
      <div><span class="eyebrow">Provider operations</span><h1>平台账号与号池</h1><p>集中管理各 Provider 的设备、Cookie、登录态、权重与调度开关。</p></div>
      <div class="admin-heading-actions">
        <button class="secondary-button" :disabled="importingLegacy" @click="actionDialog = { kind: 'import' }"><LoaderCircle v-if="importingLegacy" class="spin" :size="16" /><RefreshCw v-else :size="16" />导入旧账号</button>
        <button class="primary-button" @click="createForm.platform = platformFilter === 'all' ? createForm.platform : platformFilter; creating = true"><Plus :size="16" />添加账号</button>
      </div>
    </div>

    <div class="pool-safety-note"><ShieldAlert :size="17" /><div><strong>账号设备与登录态加密保存</strong><span>MySQL 仅保存 AES-GCM 密文；调用时按账号租约物化为受限运行缓存，完成后重新加密回写。</span></div></div>
    <div v-if="!['ctrip', 'bilibili'].includes(platformFilter)" class="pool-safety-note">
      <LogIn :size="17" />
      <div>
        <strong>平台浏览器登录助手 · {{ providerLoginExtensionReady ? `已连接 v${providerLoginExtensionVersion}` : providerLoginExtensionVersion ? `需升级（当前 v${providerLoginExtensionVersion}）` : "未连接" }}</strong>
        <span>同一个助手会为微博 Web、抖音、雪球、Reddit、X/Twitter、闲鱼、京东、牛客、Instagram、脉脉、剑鱼标讯、懂车帝和 LinkedIn 账号打开全新的无痕登录窗口。安装后需开启“允许在无痕模式下运行”。<a :href="`/extensions/byrecc-provider-login-extension.zip?v=${expectedProviderLoginExtensionVersion}`" download>下载扩展 v{{ expectedProviderLoginExtensionVersion }}（解压后加载）</a></span>
      </div>
    </div>
    <div v-if="platformFilter === 'ctrip'" class="pool-safety-note"><LogIn :size="17" /><div><strong>携程短信验证码登录</strong><span>点击验证码登录，系统会向账号已保存的手机号发送短信，输入验证码即可登录。登录凭证自动加密保存。</span></div></div>
    <p v-if="error" class="form-error">{{ error }}</p>
    <p v-if="providerBrowserError" class="form-error">{{ providerBrowserError }}</p>
    <p v-if="providerBrowserProgress" class="credential-note">{{ providerBrowserProgress }}</p>
    <div v-if="loading" class="admin-loading"><LoaderCircle class="spin" :size="20" />正在读取号池</div>
    <section v-else class="provider-list-panel">
      <header class="provider-list-toolbar">
        <div class="platform-filter" role="tablist" aria-label="平台筛选">
          <button v-for="filter in [{ value: 'all', label: '全部账号' }, { value: 'rednote', label: '小红书' }, { value: 'zhihu', label: '知乎' }, { value: 'douban', label: '豆瓣' }, { value: 'douyin', label: '抖音' }, { value: 'weibo', label: '微博 CLI' }, { value: 'weibo_web', label: '微博 Web' }, { value: 'bilibili', label: 'B站' }, { value: 'xueqiu', label: '雪球' }, { value: 'reddit', label: 'Reddit' }, { value: 'twitter', label: 'X' }, { value: 'xianyu', label: '闲鱼' }, { value: 'jd', label: '京东' }, { value: 'nowcoder', label: '牛客' }, { value: 'instagram', label: 'Instagram' }, { value: 'maimai', label: '脉脉' }, { value: 'jianyu', label: '剑鱼标讯' }, { value: 'dongchedi', label: '懂车帝' }, { value: 'ctrip', label: '携程' }, { value: 'linkedin', label: 'LinkedIn' }]" :key="filter.value" type="button" role="tab" :aria-selected="platformFilter === filter.value" :class="{ 'platform-filter__button--active': platformFilter === filter.value }" class="platform-filter__button" @click="platformFilter = filter.value as PlatformFilter">
            {{ filter.label }}<span>{{ platformCounts[filter.value as PlatformFilter] }}</span>
          </button>
        </div>
        <div class="provider-list-summary">
          <span><i class="provider-list-summary__dot provider-list-summary__dot--online"></i>{{ loggedInCount }} 已登录</span>
          <span><i class="provider-list-summary__dot"></i>{{ poolAccountCount }} 在号池</span>
          <button type="button" class="icon-button" aria-label="刷新账号列表" title="刷新账号列表" @click="load"><RefreshCw :size="15" /></button>
        </div>
      </header>

      <div class="provider-list-head" aria-hidden="true">
        <span>账号</span><span>登录状态</span><span>号池调度</span><span>24h 调用</span><span>操作</span>
      </div>
      <div v-if="filteredAccounts.length === 0" class="admin-empty provider-list-empty">
        {{ platformFilter === "all" ? "尚未添加平台账号。" : `尚未添加${platformLabel(platformFilter)}账号。` }}
      </div>
      <div v-else class="provider-account-list">
        <article v-for="account in filteredAccounts" :key="account.id" class="provider-account-row">
          <div class="provider-account-identity">
            <span class="platform-mark" :class="`platform-mark--${account.platform}`">{{ platformMark(account.platform) }}</span>
            <div>
              <strong>{{ account.display_name }}</strong>
              <span class="provider-account-identifier">{{ account.login_identifier || "未配置登录账号" }}</span>
              <span><code>{{ account.state_name }}</code><small>{{ platformLabel(account.platform) }}</small></span>
            </div>
          </div>
          <div class="provider-login-status">
            <span class="status-pill" :class="{ 'status-pill--active': account.login_status === 'logged_in', 'status-pill--expired': ['expired', 'error'].includes(account.login_status) }"><i></i>{{ loginStatusLabel(account.login_status) }}</span>
            <small :class="{ 'credential-ready': account.credentials_ready }">{{ credentialStatus(account) }}</small>
          </div>
          <div class="provider-pool-control">
            <label class="pool-toggle"><input type="checkbox" :checked="account.pool_enabled" :disabled="account.login_status !== 'logged_in'" @change="patchAccount(account, { pool_enabled: ($event.target as HTMLInputElement).checked })" /><span>{{ account.pool_enabled ? "已进入号池" : "未进入号池" }}</span></label>
            <label class="weight-control"><span>权重</span><input class="weight-input" type="number" min="1" max="100" :value="account.weight" @change="patchAccount(account, { weight: Number(($event.target as HTMLInputElement).value) })" /></label>
          </div>
          <div class="provider-last-login"><strong>{{ account.calls_24h }} 次 · 成功率 {{ accountSuccessRate(account) }}</strong><small>{{ account.failed_calls_24h }} 失败 · 均耗时 {{ account.avg_duration_ms_24h === null ? "—" : `${account.avg_duration_ms_24h}ms` }} · 最近 {{ formatDate(account.last_call_at) }}</small></div>
          <div class="provider-account-actions">
            <button v-if="browserLoginPlatforms.includes(account.platform) && account.login_status !== 'logged_in' && providerBrowserFlow?.account.id !== account.id" class="secondary-button" :disabled="!providerLoginExtensionReady || Boolean(actionAccountId)" :title="providerLoginExtensionReady ? `打开独立${platformLabel(account.platform)}登录窗口` : '请先安装平台登录助手并刷新页面'" @click="startProviderBrowserLogin(account)"><LogIn :size="14" />浏览器登录</button>
            <button v-if="providerBrowserFlow?.account.id === account.id" class="secondary-button" type="button" title="立即重新读取独立窗口中的登录凭证" @click="captureProviderBrowserLogin"><RefreshCw :size="14" />重新检测</button>
            <button v-if="account.platform === 'bilibili'" class="secondary-button" :disabled="Boolean(actionAccountId)" @click="deviceImportAccount = account; deviceImportJson = ''; deviceImportError = ''">导入 iOS 设备</button>
            <button v-if="account.login_status !== 'logged_in' && ['rednote', 'douban', 'weibo', 'ctrip', 'bilibili'].includes(account.platform)" class="secondary-button" :disabled="(!['ctrip', 'bilibili'].includes(account.platform) && !account.sms_login_ready) || actionAccountId === account.id" :title="['ctrip', 'bilibili'].includes(account.platform) || account.sms_login_ready ? '发送手机验证码并登录' : '该账号缺少手机号'" @click="sendSmsCode(account)"><LoaderCircle v-if="actionAccountId === account.id" class="spin" :size="14" /><MessageSquareText v-else :size="14" />验证码登录</button>
            <button v-if="account.platform === 'bilibili' && account.sms_captcha" class="secondary-button" :disabled="Boolean(actionAccountId)" @click="captchaAccount = account; captchaError = ''">完成人机验证</button>
            <button v-if="['ctrip', 'bilibili'].includes(account.platform) && account.sms_login_pending" class="secondary-button" :disabled="Boolean(actionAccountId)" @click="smsLoginAccount = account; smsCode = ''; smsError = ''">输入验证码</button>
            <button v-if="account.platform !== 'ctrip' && account.login_status !== 'logged_in' && account.password_login_ready" class="text-button" :disabled="actionAccountId === account.id" @click="runAction(account, 'login')"><LogIn :size="14" />{{ account.platform === "rednote" ? "密码登录" : account.platform === "weibo" ? "启用会话" : cookiePlatforms.includes(account.platform) ? "校验 Cookie" : "登录" }}</button>
            <button v-if="account.login_status === 'logged_in'" class="secondary-button" :disabled="actionAccountId === account.id" @click="actionDialog = { kind: 'logout', account }"><LogOut :size="14" />登出</button>
            <button class="text-button" :disabled="actionAccountId === account.id" @click="cooldownSeconds = '300'; actionDialog = { kind: 'cooldown', account }"><CircleOff :size="14" />冷却</button>
            <button class="text-button" :disabled="actionAccountId === account.id" @click="runAction(account, 'healthy')"><HeartPulse :size="14" />恢复健康</button>
            <button class="text-button provider-delete-button" :disabled="actionAccountId === account.id" @click="actionDialog = { kind: 'delete', account }"><Trash2 :size="14" />删除</button>
          </div>
          <p v-if="account.last_error" class="provider-error">{{ account.last_error }}</p>
        </article>
      </div>
    </section>

    <div v-if="creating" class="modal-backdrop" @click.self="creating = false">
      <form class="modal" @submit.prevent="createAccount">
        <div class="modal__heading"><div><span class="eyebrow">New provider account</span><h2>添加平台账号</h2></div><button type="button" class="icon-button" @click="creating = false"><X :size="18" /></button></div>
        <div class="admin-form-grid">
          <label class="field"><span>平台</span><AppSelect v-model="createForm.platform" aria-label="平台" :options="platformOptions" /></label>
          <label class="field"><span>显示名称</span><input v-model="createForm.display_name" required maxlength="120" placeholder="例如：小红书采集 01" /></label>
          <label class="field"><span>状态名称</span><input v-model="createForm.state_name" required maxlength="64" pattern="[A-Za-z0-9][A-Za-z0-9_.-]{0,63}" placeholder="rednote-01" /><small>用于设备和会话目录，创建后不可修改。</small></label>
          <label class="field"><span>调度权重</span><input v-model.number="createForm.weight" type="number" min="1" max="100" required /></label>
          <label class="field"><span>{{ cookiePlatforms.includes(createForm.platform) ? '账号标识' : '平台账号 / 手机号' }}</span><input v-model="createForm.username" autocomplete="username" required maxlength="320" :placeholder="['weibo', 'ctrip', 'bilibili'].includes(createForm.platform) ? '用于接收短信登录验证码' : cookiePlatforms.includes(createForm.platform) ? '仅用于后台识别该 Cookie 账号' : '用于标识和后续登录'" /></label>
          <label v-if="['rednote', 'douban', 'weibo', 'bilibili'].includes(createForm.platform)" class="field"><span>国家区号</span><input v-model="createForm.zone" inputmode="numeric" maxlength="4" pattern="\d{1,4}" required /></label>
          <label v-if="['rednote', 'zhihu'].includes(createForm.platform)" class="field admin-form-grid__full"><span>{{ createForm.platform === "rednote" ? "登录密码（可选）" : "登录密码" }}</span><input v-model="createForm.password" type="password" autocomplete="new-password" :required="createForm.platform === 'zhihu'" /><small>{{ createForm.platform === "rednote" ? "留空即可使用手机验证码登录；填写后同时保留密码登录。" : "创建后会随账号状态整体加密保存。" }}</small></label>
          <p v-if="createForm.platform === 'bilibili'" class="credential-note">B站使用 iOS 设备登录。可导入 bilibili-cli ios 分支的完整设备 JSON，或留空后短信登录；旧网页 Cookie 账号需重新登录。</p>
          <label v-if="['douban', 'weibo', 'bilibili', ...cookiePlatforms].includes(createForm.platform)" class="field admin-form-grid__full"><span>{{ ["weibo", "bilibili"].includes(createForm.platform) ? "已有设备与会话 JSON（可选）" : browserLoginPlatforms.includes(createForm.platform) ? `${platformLabel(createForm.platform)} Cookie（可选）` : cookiePlatforms.includes(createForm.platform) ? `${platformLabel(createForm.platform)} Cookie（必填）` : "已有豆瓣状态 JSON（可选）" }}</span><textarea v-model="createForm.state_json" rows="8" spellcheck="false" :required="cookiePlatforms.includes(createForm.platform) && !browserLoginPlatforms.includes(createForm.platform)" :placeholder="['weibo', 'bilibili'].includes(createForm.platform) ? '留空将自动创建独立设备，并通过手机号验证码登录' : browserLoginPlatforms.includes(createForm.platform) ? '可留空，创建账号后使用平台登录助手' : cookiePlatforms.includes(createForm.platform) ? 'Cookie Header、Cookie 字典或 Cookie-Editor JSON' : '{ &quot;access_token&quot;: &quot;...&quot; }'" /><small>{{ ["weibo", "bilibili"].includes(createForm.platform) ? "可导入已有会话；留空则首次发送验证码时自动初始化设备。" : browserLoginPlatforms.includes(createForm.platform) ? "推荐留空；创建后点击浏览器登录，Cookie 会自动校验并加密保存。" : cookiePlatforms.includes(createForm.platform) ? "Cookie 仅进入加密账号池，不会对外暴露。" : "提交后只保留加密状态，不在账号列表或日志中回显。" }}</small></label>
        </div>
        <p class="credential-note">每个账号使用独立设备和会话状态。状态以数据库密文为准，本地文件只是可丢弃的 SDK 运行缓存。</p>
        <div class="modal__actions"><button type="button" class="secondary-button" @click="creating = false">取消</button><button class="primary-button" :disabled="submitting"><LoaderCircle v-if="submitting" class="spin" :size="16" />创建账号</button></div>
      </form>
    </div>

    <div v-if="deviceImportAccount" class="modal-backdrop">
      <form class="modal" @submit.prevent="importBilibiliDevice">
        <div class="modal__heading"><h2>导入 Bilibili iOS 设备</h2><button type="button" class="icon-button" :disabled="Boolean(actionAccountId)" @click="deviceImportAccount = null; deviceImportJson = ''"><X :size="18" /></button></div>
        <p class="credential-note">粘贴 bilibili-cli ios 分支登录后的完整设备 JSON。设备身份与 App 凭证将一起加密保存；网页 Cookie 不适用。</p>
        <textarea v-model="deviceImportJson" rows="10" required spellcheck="false" autocomplete="off" aria-label="iOS 设备 JSON" />
        <p v-if="deviceImportError" class="form-error">{{ deviceImportError }}</p>
        <div class="modal__actions"><button class="primary-button" :disabled="Boolean(actionAccountId)">校验并保存</button></div>
      </form>
    </div>

    <div v-if="smsPhoneAccount" class="modal-backdrop">
      <form class="modal" @submit.prevent="sendSmsCode(smsPhoneAccount, smsPhone)">
        <div class="modal__heading"><h2>{{ platformLabel(smsPhoneAccount.platform) }}验证码登录</h2><button type="button" class="icon-button" :disabled="Boolean(actionAccountId)" @click="smsPhoneAccount = null"><X :size="18" /></button></div>
        <p class="credential-note">此账号尚未保存手机号，请补充用于接收验证码的中国大陆手机号（+86）。保存后无需重复输入。</p>
        <label class="field"><span>手机号</span><input v-model.trim="smsPhone" type="tel" autocomplete="tel-national" pattern="1\d{10}" maxlength="11" required placeholder="11 位手机号" /></label>
        <p v-if="error" class="form-error">{{ error }}</p>
        <div class="modal__actions"><button type="button" class="secondary-button" :disabled="Boolean(actionAccountId)" @click="smsPhoneAccount = null">取消</button><button class="primary-button" :disabled="Boolean(actionAccountId)"><LoaderCircle v-if="actionAccountId" class="spin" :size="16" />发送验证码</button></div>
      </form>
    </div>

    <div v-if="captchaAccount" class="modal-backdrop">
      <section class="modal" aria-label="B站人机验证">
        <div class="modal__heading"><h2>完成人机验证</h2><button type="button" class="icon-button" :disabled="Boolean(actionAccountId)" @click="captchaAccount = null"><X :size="18" /></button></div>
        <p class="credential-note">B站要求先验证本次登录。请完成下方验证，通过后会继续发送短信。</p>
        <iframe ref="captchaFrame" :key="captchaUrl" :src="captchaUrl" title="B站人机验证" sandbox="allow-scripts" referrerpolicy="no-referrer" style="width:100%;height:400px;border:0" />
        <p v-if="captchaError" class="form-error">{{ captchaError }}</p>
        <div class="modal__actions"><button type="button" class="secondary-button" :disabled="Boolean(actionAccountId)" @click="sendSmsCode(captchaAccount)">重新获取验证</button></div>
      </section>
    </div>

    <div v-if="smsLoginAccount" class="modal-backdrop" @click.self="smsLoginAccount = null">
      <form class="modal" @submit.prevent="verifySmsCode">
        <div class="modal__heading">
          <div><span class="eyebrow">Provider SMS login</span><h2>输入手机验证码</h2></div>
          <button type="button" class="icon-button" @click="smsLoginAccount = null"><X :size="18" /></button>
        </div>
        <p class="credential-note">验证码已发送至 {{ smsLoginAccount.login_identifier }}。校验通过后会自动完成登录并将账号加入号池。</p>
        <label class="field">
          <span>6 位验证码</span>
          <input v-model.trim="smsCode" inputmode="numeric" autocomplete="one-time-code" maxlength="6" pattern="\d{4,6}" autofocus required placeholder="请输入短信验证码" />
        </label>
        <p v-if="smsError" class="form-error">{{ smsError }}</p>
        <div class="modal__actions">
          <button type="button" class="text-button" :disabled="smsVerifying" @click="resendSmsCode">重新发送</button>
          <button type="button" class="secondary-button" :disabled="smsVerifying" @click="smsLoginAccount = null">取消</button>
          <button class="primary-button" :disabled="smsVerifying || !smsCodeValid"><LoaderCircle v-if="smsVerifying" class="spin" :size="16" />验证并登录</button>
        </div>
      </form>
    </div>

    <AppActionDialog
      v-model:input-value="cooldownSeconds"
      :open="Boolean(actionDialog)"
      eyebrow="Provider operation"
      :title="actionDialog?.kind === 'import' ? '导入旧账号' : actionDialog?.kind === 'logout' ? '确认登出账号' : actionDialog?.kind === 'delete' ? '确认删除账号' : '设置账号冷却'"
      :description="actionDialog?.kind === 'import' ? '读取服务器现有登录状态并初始化数据库记录。' : actionDialog?.kind === 'logout' ? '登出后该账号会同时移出调度号池。' : actionDialog?.kind === 'delete' ? '账号会立即移出号池，设备、会话和登录凭据将永久删除；历史调用与审计记录仍会保留。' : '冷却期间该账号不会参与号池调度。'"
      :detail="actionDialog?.kind === 'import' ? '小红书与知乎默认登录态' : actionDialog?.account?.display_name ?? ''"
      :confirm-label="actionDialog?.kind === 'import' ? '确认导入' : actionDialog?.kind === 'logout' ? '确认登出' : actionDialog?.kind === 'delete' ? '永久删除' : '开始冷却'"
      :danger="actionDialog?.kind === 'logout' || actionDialog?.kind === 'delete'"
      :busy="importingLegacy || Boolean(actionAccountId)"
      :input-label="actionDialog?.kind === 'cooldown' ? '冷却秒数' : ''"
      :input-hint="actionDialog?.kind === 'cooldown' ? '输入 0 可立即解除冷却。' : ''"
      input-type="number"
      @close="actionDialog = null"
      @confirm="confirmProviderAction"
    />
  </section>
</template>
